/*
 * "Ships by" date on product pages (snippets/ships-by.liquid).
 *
 * The shown date is when the order leaves the warehouse, not a delivery date.
 * Each item ships on the later of the regular date and its own date (variant
 * metafield → product metafield → rule in Theme settings → Ship dates). The
 * line shows the latest date of the selected variant and any selected
 * Essential Upsell add-ons.
 *
 * All "today" logic runs here, in the visitor's browser, because product pages
 * are served from cache.
 */
(function () {
  if (window.shipsByLoaded) return;
  window.shipsByLoaded = true;

  const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

  // Parse YYYY-MM-DD as a local date. new Date('2026-11-01') would be UTC
  // midnight and show the previous day in US timezones.
  function parseDate(value) {
    const match = ISO_DATE.exec(String(value || '').trim());
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.getMonth() === Number(match[2]) - 1 ? date : null;
  }

  function today() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  function addDays(start, days) {
    const date = new Date(start);
    date.setDate(date.getDate() + days);
    return date;
  }

  function addBusinessDays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) added++;
    }
    return date;
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  // One rule per line:
  //   product-handle = YYYY-MM-DD
  //   product-handle, Option name: Option value = YYYY-MM-DD
  function parseRules(text) {
    return String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#'))
      .map((line) => {
        const equals = line.lastIndexOf('=');
        if (equals === -1) return null;
        const date = parseDate(line.slice(equals + 1));
        if (!date) return null;
        const target = line.slice(0, equals);
        const comma = target.indexOf(',');
        const rule = { handle: normalize(comma === -1 ? target : target.slice(0, comma)), date };
        if (comma !== -1) {
          const option = target.slice(comma + 1);
          const colon = option.indexOf(':');
          if (colon === -1) return null;
          rule.option = normalize(option.slice(0, colon));
          rule.value = normalize(option.slice(colon + 1));
        }
        return rule;
      })
      .filter(Boolean);
  }

  // Latest matching rule date for a product handle and its selected options
  // ([{ name, value }]). Option names are trimmed: the W2 option names in admin
  // end with a space.
  function ruleDate(rules, handle, options) {
    let latest = null;
    rules.forEach((rule) => {
      if (rule.handle !== normalize(handle)) return;
      if (rule.option) {
        const matches = (options || []).some(
          (option) => normalize(option.name) === rule.option && normalize(option.value) === rule.value
        );
        if (!matches) return;
      }
      if (!latest || rule.date > latest) latest = rule.date;
    });
    return latest;
  }

  class ShipsBy {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-ships-by-config]').textContent);
      this.rules = parseRules(this.config.rules);
      this.dateElement = root.querySelector('[data-ships-by-date]');
      this.variantId = this.config.selectedVariant;
      this.variantSelects =
        root.closest('product-info, section')?.querySelector('variant-selects') ||
        document.querySelector('variant-selects');

      this.setFlag();
      this.bindEvents();
      this.update();
    }

    setFlag() {
      const flag = this.root.querySelector('[data-ships-by-flag]');
      const iso = this.config.country;
      if (!flag || !iso || iso.length !== 2) return;
      flag.textContent = String.fromCodePoint(127397 + iso.charCodeAt(0), 127397 + iso.charCodeAt(1));
    }

    bindEvents() {
      const scheduleUpdate = this.scheduleUpdate.bind(this);

      // VariantSelects sets currentVariant in its own change listener, which
      // runs before this one. currentVariant is undefined for combinations
      // that do not exist.
      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', () => {
          this.variantId = this.variantSelects.currentVariant ? this.variantSelects.currentVariant.id : null;
          this.update();
        });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event.data.variant) return;
          this.variantId = event.data.variant.id;
          this.update();
        });
      }

      // Essential Upsell add-on checkboxes stop change/click from bubbling, so
      // listen in the capture phase. The app writes its state in an effect after
      // the event, so read it a little later. The MutationObserver catches
      // anything else the widget re-renders (variant pickers, late mount).
      document.addEventListener('change', scheduleUpdate, true);
      document.addEventListener('click', scheduleUpdate, true);
      const observeRoot = this.root.closest('product-info, section') || document.body;
      new MutationObserver(scheduleUpdate).observe(observeRoot, { childList: true, subtree: true });
    }

    scheduleUpdate() {
      clearTimeout(this.updateTimer);
      this.updateTimer = setTimeout(() => this.update(), 150);
    }

    currentVariant() {
      return this.config.variants.find((variant) => variant.id === Number(this.variantId)) || null;
    }

    regularDate() {
      const start = today();
      let date = null;
      if (this.config.mode === 'fixed') {
        const fixed = parseDate(this.config.fixedDate);
        if (fixed && fixed >= start) date = fixed;
      }
      if (!date) date = addBusinessDays(start, Number(this.config.businessDays) || 0);
      return this.config.bufferScope === 'product' ? date : addDays(date, Number(this.config.bufferDays) || 0);
    }

    // An item ships on its own date if that is later than the regular date,
    // otherwise on the regular date. Own dates can only move the date later,
    // and past own dates fall back to the regular date.
    itemDate(ownDate, regular) {
      if (!ownDate) return regular;
      const date = this.config.bufferScope === 'regular' ? ownDate : addDays(ownDate, Number(this.config.bufferDays) || 0);
      return date > regular ? date : regular;
    }

    mainItemDate(variant, regular) {
      const options = this.config.optionNames.map((name, index) => ({ name, value: variant.options[index] }));
      const ownDate =
        parseDate(variant.date) ||
        parseDate(this.config.productDate) ||
        ruleDate(this.rules, this.config.handle, options);
      return this.itemDate(ownDate, regular);
    }

    // Selected Essential Upsell add-ons: window.essentialUpsellProductStates is
    // { [funnelId]: { productStates: { [productHandle]: { isSelected, currentVariant } } } }.
    addonDates(regular) {
      const funnels = window.essentialUpsellProductStates;
      if (!funnels || typeof funnels !== 'object') return [];
      const dates = [];
      Object.values(funnels).forEach((funnel) => {
        const states = (funnel && funnel.productStates) || {};
        Object.entries(states).forEach(([handle, state]) => {
          if (!state || !state.isSelected) return;
          const options = (state.currentVariant && state.currentVariant.selectedOptions) || [];
          dates.push(this.itemDate(ruleDate(this.rules, handle, options), regular));
        });
      });
      return dates;
    }

    format(date) {
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      if (date.getFullYear() !== today().getFullYear()) options.year = 'numeric';
      try {
        return new Intl.DateTimeFormat(this.config.locale || 'en', options).format(date);
      } catch (error) {
        return new Intl.DateTimeFormat('en', options).format(date);
      }
    }

    update() {
      const variant = this.currentVariant();
      if (!variant || !variant.available) {
        this.root.hidden = true;
        return;
      }

      const regular = this.regularDate();
      let latest = this.mainItemDate(variant, regular);
      this.addonDates(regular).forEach((date) => {
        if (date > latest) latest = date;
      });

      const text = this.format(latest);
      if (this.dateElement.textContent !== text) this.dateElement.textContent = text;
      this.root.hidden = false;
    }
  }

  function init(scope) {
    scope.querySelectorAll('[data-ships-by]').forEach((root) => {
      if (root.shipsBy) return;
      try {
        root.shipsBy = new ShipsBy(root);
      } catch (error) {
        console.error('ships-by', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
